import React, { useState, useEffect } from 'react';
import { getUsers, inviteUser, updateUser, getSettings, updateSetting, getIPAllowlist, addIP, removeIP, getSlotLabelsRacks, getSlotLabelsPDF, generateAllSlotLabels, getLabelAffixingManual, getQRStatus, preGenerateQRCodes } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { Users, Settings, Shield, Plus, Trash2, Globe, Tag, Download, FileText, Loader2, Package, CheckCircle, AlertTriangle, Database, Mail, BookOpen, Wrench } from 'lucide-react';
import { ROW_COLORS } from '@/lib/constants';
import AdminDataTab from '@/components/AdminDataTab';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function AdminPage() {
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState({});
  const [ips, setIps] = useState([]);
  const [newUser, setNewUser] = useState({ email: '', full_name: '', role: 'user', password: '' });
  const [newIp, setNewIp] = useState({ ip_address: '', notes: '' });
  const [showInvite, setShowInvite] = useState(false);
  
  // Slot labels state
  const [racks, setRacks] = useState([]);
  const [labelStats, setLabelStats] = useState({ total_racks: 0, total_slots: 0 });
  const [generatingAll, setGeneratingAll] = useState(false);
  const [generateProgress, setGenerateProgress] = useState(0);
  const [downloadingRack, setDownloadingRack] = useState(null);
  const [qrStatus, setQRStatus] = useState({ total: 764, ready: 0, percentage: 0 });
  const [generatingQR, setGeneratingQR] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);

  useEffect(() => {
    loadData();
    loadRacks();
    loadQRStatus();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, settingsRes, ipsRes] = await Promise.all([getUsers(), getSettings(), getIPAllowlist()]);
      setUsers(usersRes.data);
      setSettings(settingsRes.data);
      setIps(ipsRes.data);
    } catch (err) { console.error(err); }
  };

  const loadRacks = async () => {
    try {
      const res = await getSlotLabelsRacks();
      setRacks(res.data.racks || []);
      setLabelStats({ total_racks: res.data.total_racks, total_slots: res.data.total_slots });
    } catch (err) { console.error(err); }
  };

  const loadQRStatus = async () => {
    try {
      const res = await getQRStatus();
      setQRStatus(res.data);
    } catch (err) { console.error(err); }
  };

  const handlePreGenerateQR = async () => {
    setGeneratingQR(true);
    try {
      await preGenerateQRCodes();
      toast.success('All 764 slot QR codes generated!');
      await loadQRStatus();
    } catch (err) {
      toast.error('Failed to generate QR codes');
    }
    setGeneratingQR(false);
  };

  const handleInvite = async () => {
    if (!newUser.email || !newUser.full_name || !newUser.password) {
      return toast.error('All fields are required');
    }
    try {
      await inviteUser(newUser);
      toast.success('User invited');
      setShowInvite(false);
      setNewUser({ email: '', full_name: '', role: 'user', password: '' });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleToggleUser = async (userId, isActive) => {
    try {
      await updateUser(userId, { is_active: !isActive });
      toast.success(isActive ? 'User deactivated' : 'User activated');
      loadData();
    } catch (err) { toast.error('Failed'); }
  };

  const handleSettingChange = async (key, value) => {
    try {
      await updateSetting(key, value);
      setSettings(prev => ({ ...prev, [key]: value }));
      toast.success('Setting updated');
    } catch (err) { toast.error('Failed'); }
  };

  const handleAddIP = async () => {
    if (!newIp.ip_address) return toast.error('Enter IP address');
    try {
      await addIP(newIp);
      toast.success('IP added');
      setNewIp({ ip_address: '', notes: '' });
      loadData();
    } catch (err) { toast.error('Failed'); }
  };

  const handleRemoveIP = async (id) => {
    try {
      await removeIP(id);
      toast.success('IP removed');
      loadData();
    } catch (err) { toast.error('Failed'); }
  };

  const downloadPdfFromUrl = async (pdfUrl, filename) => {
    try {
      const pdfRes = await fetch(`${API_BASE}${pdfUrl}`);
      const blob = await pdfRes.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      // Fallback: open in new tab
      window.open(`${API_BASE}${pdfUrl}`, '_blank');
    }
  };

  const handleDownloadRackLabels = async (rackCode) => {
    setDownloadingRack(rackCode);
    try {
      const res = await getSlotLabelsPDF(rackCode);
      if (res.data.url) {
        toast.success(`Labels for Rack ${rackCode} generated. Downloading...`);
        await downloadPdfFromUrl(res.data.url, `Rack_${rackCode}_Labels.pdf`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate labels');
    }
    setTimeout(() => setDownloadingRack(null), 2000);
  };

  const handleGenerateAllLabels = async () => {
    setGeneratingAll(true);
    setGenerateProgress(0);
    
    const interval = setInterval(() => {
      setGenerateProgress(prev => Math.min(prev + 2, 95));
    }, 200);
    
    try {
      const res = await generateAllSlotLabels();
      clearInterval(interval);
      setGenerateProgress(100);
      toast.success('All slot labels generated! Downloading...');
      
      setTimeout(async () => {
        await downloadPdfFromUrl(res.data.url, 'Sturdfix_WMS_All_Slot_Labels.zip');
        setGeneratingAll(false);
        setGenerateProgress(0);
      }, 500);
    } catch (err) {
      clearInterval(interval);
      toast.error('Failed to generate labels');
      setGeneratingAll(false);
      setGenerateProgress(0);
    }
  };

  const handleDownloadManual = async () => {
    try {
      const res = await getLabelAffixingManual();
      toast.success('Downloading Label Affixing Manual...');
      await downloadPdfFromUrl(res.data.url, 'Sturdfix_WMS_Label_Affixing_Manual.pdf');
    } catch (err) {
      toast.error('Failed to generate manual');
    }
  };

  // Group racks by row
  const racksByRow = racks.reduce((acc, rack) => {
    const row = rack.row;
    if (!acc[row]) acc[row] = [];
    acc[row].push(rack);
    return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="admin-page">
      <h1 className="text-xl font-bold text-gray-900">Admin Panel</h1>

      <Tabs defaultValue="users">
        <TabsList className="grid grid-cols-7 w-full max-w-3xl">
          <TabsTrigger value="users" data-testid="tab-users"><Users size={14} className="mr-1" /> Users</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings"><Settings size={14} className="mr-1" /> Settings</TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security"><Shield size={14} className="mr-1" /> Security</TabsTrigger>
          <TabsTrigger value="labels" data-testid="tab-labels"><Tag size={14} className="mr-1" /> Labels</TabsTrigger>
          <TabsTrigger value="backup" data-testid="tab-backup"><Database size={14} className="mr-1" /> Backup</TabsTrigger>
          <TabsTrigger value="data" data-testid="tab-data"><Wrench size={14} className="mr-1" /> Data</TabsTrigger>
          <TabsTrigger value="guide" data-testid="tab-guide"><BookOpen size={14} className="mr-1" /> Guide</TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-3">
          <Dialog open={showInvite} onOpenChange={setShowInvite}>
            <DialogTrigger asChild>
              <Button className="h-9 text-xs" style={{ backgroundColor: '#1a2fce' }} data-testid="invite-user-btn">
                <Plus size={14} className="mr-1" /> Invite User
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Invite New User</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs">Full Name</Label><Input value={newUser.full_name} onChange={e => setNewUser(p => ({...p, full_name: e.target.value}))} className="h-9 text-sm" data-testid="invite-name" /></div>
                <div><Label className="text-xs">Email</Label><Input type="email" value={newUser.email} onChange={e => setNewUser(p => ({...p, email: e.target.value}))} className="h-9 text-sm" data-testid="invite-email" /></div>
                <div><Label className="text-xs">Password</Label><Input type="password" value={newUser.password} onChange={e => setNewUser(p => ({...p, password: e.target.value}))} className="h-9 text-sm" data-testid="invite-password" /></div>
                <div><Label className="text-xs">Role</Label>
                  <Select value={newUser.role} onValueChange={v => setNewUser(p => ({...p, role: v}))}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">Staff (USER)</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleInvite} className="w-full h-10" style={{ backgroundColor: '#1a2fce' }} data-testid="confirm-invite">Invite User</Button>
              </div>
            </DialogContent>
          </Dialog>

          {users.map(u => (
            <Card key={u.id} className="border-0 shadow-sm">
              <CardContent className="p-3 flex items-center gap-3">
                <div className="flex-1">
                  <div className="text-sm font-semibold">{u.full_name}</div>
                  <div className="text-xs text-gray-500">{u.email}</div>
                </div>
                <Badge variant="outline" className="text-xs capitalize">{u.role}</Badge>
                <Switch checked={u.is_active} onCheckedChange={() => handleToggleUser(u.id, u.is_active)} />
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-3">
          {[
            { key: 'slow_moving_days', label: 'Slow Moving Threshold (days)', type: 'number' },
            { key: 'pallet_capacity_default', label: 'Default Pallet Capacity', type: 'number' },
            { key: 'auto_lock_minutes', label: 'Auto Lock Timeout (minutes)', type: 'number' },
            { key: 'variance_tolerance_pct', label: 'Variance Tolerance (%)', type: 'number' },
            { key: 'abandoned_session_hours', label: 'Abandoned Session Threshold (hours)', type: 'number', min: 4 },
          ].map(s => (
            <Card key={s.key} className="border-0 shadow-sm">
              <CardContent className="p-3 flex items-center gap-3">
                <div className="flex-1">
                  <Label className="text-xs">{s.label}</Label>
                  {s.key === 'abandoned_session_hours' && (
                    <div className="text-[10px] text-gray-400">Minimum: 4 hours. Sessions exceeding this will be flagged as abandoned.</div>
                  )}
                </div>
                <Input 
                  type={s.type} 
                  min={s.min}
                  value={settings[s.key] || ''} 
                  onChange={e => setSettings(p => ({...p, [s.key]: e.target.value}))}
                  onBlur={() => {
                    if (s.key === 'abandoned_session_hours' && parseInt(settings[s.key]) < 4) {
                      toast.error('Minimum threshold is 4 hours');
                      setSettings(p => ({...p, [s.key]: '4'}));
                      handleSettingChange(s.key, '4');
                    } else {
                      handleSettingChange(s.key, settings[s.key]);
                    }
                  }} 
                  className="w-24 h-8 text-sm text-right" 
                />
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="space-y-3">
          {/* IP Bypass Toggle */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-3 flex items-center gap-3">
              <Globe size={16} className="text-[#1a2fce]" />
              <div className="flex-1"><Label className="text-xs">Bypass IP Check (Testing)</Label></div>
              <Switch
                checked={settings.bypass_ip_check === 'true'}
                onCheckedChange={v => handleSettingChange('bypass_ip_check', v ? 'true' : 'false')}
                data-testid="bypass-ip-toggle"
              />
            </CardContent>
          </Card>

          <div className="text-sm font-semibold text-gray-700">IP Allowlist</div>
          {ips.map(ip => (
            <Card key={ip.id} className="border-0 shadow-sm">
              <CardContent className="p-3 flex items-center gap-3">
                <div className="flex-1">
                  <div className="font-mono text-sm">{ip.ip_address}</div>
                  <div className="text-xs text-gray-400">{ip.notes}</div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => handleRemoveIP(ip.id)}><Trash2 size={14} className="text-red-500" /></Button>
              </CardContent>
            </Card>
          ))}
          <div className="flex gap-2">
            <Input value={newIp.ip_address} onChange={e => setNewIp(p => ({...p, ip_address: e.target.value}))} placeholder="IP Address" className="h-9 text-sm" data-testid="new-ip" />
            <Input value={newIp.notes} onChange={e => setNewIp(p => ({...p, notes: e.target.value}))} placeholder="Notes" className="h-9 text-sm" />
            <Button onClick={handleAddIP} className="h-9" style={{ backgroundColor: '#1a2fce' }} data-testid="add-ip-btn"><Plus size={14} /></Button>
          </div>
        </TabsContent>

        {/* Label Generator Tab */}
        <TabsContent value="labels" className="space-y-4">
          {/* Summary Card */}
          <Card className="border-0 shadow-sm bg-gradient-to-r from-[#1a2fce]/5 to-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-800">Slot Label Generator</h3>
                  <p className="text-xs text-gray-500 mt-1">{labelStats.total_racks} racks · {labelStats.total_slots} total slots</p>
                </div>
                <Package size={32} className="text-[#1a2fce] opacity-50" />
              </div>
            </CardContent>
          </Card>

          {/* QR Status Card */}
          <Card className={`border-0 shadow-sm ${qrStatus.percentage === 100 ? 'bg-green-50' : 'bg-amber-50'}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {qrStatus.percentage === 100 ? (
                    <CheckCircle size={20} className="text-green-600" />
                  ) : (
                    <AlertTriangle size={20} className="text-amber-600" />
                  )}
                  <div>
                    <div className="text-sm font-medium text-gray-800">
                      {qrStatus.ready}/{qrStatus.total} slot QR codes ready
                    </div>
                    <div className="text-xs text-gray-500">
                      {qrStatus.percentage === 100 
                        ? 'All QR codes pre-generated for instant label generation'
                        : 'Pre-generate QR codes for faster label generation'}
                    </div>
                  </div>
                </div>
                {qrStatus.percentage < 100 && (
                  <Button
                    size="sm"
                    onClick={handlePreGenerateQR}
                    disabled={generatingQR}
                    className="h-8 text-xs"
                    style={{ backgroundColor: '#1a2fce' }}
                  >
                    {generatingQR ? (
                      <>
                        <Loader2 size={14} className="mr-1 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      'Generate All QR Codes'
                    )}
                  </Button>
                )}
              </div>
              {qrStatus.percentage > 0 && qrStatus.percentage < 100 && (
                <Progress value={qrStatus.percentage} className="h-1.5 mt-3" />
              )}
            </CardContent>
          </Card>

          {/* Quick Actions — Row-level PDF downloads */}
          <div className="grid grid-cols-2 gap-2">
            {['A', 'B', 'C', 'D'].map(row => {
              const rowSlots = { A: 240, B: 172, C: 192, D: 160 };
              return (
                <Button
                  key={row}
                  onClick={async () => {
                    setGeneratingAll(true);
                    try {
                      const token = localStorage.getItem('wms_token');
                      const res = await fetch(`${API_BASE}/api/labels/slot-labels-row/${row}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      const data = await res.json();
                      if (data.url) {
                        toast.success(`Row ${row} labels generated (${data.count} slots). Downloading...`);
                        // Download via Blob to avoid popup blocker
                        const pdfRes = await fetch(`${API_BASE}${data.url}`);
                        const blob = await pdfRes.blob();
                        const blobUrl = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = blobUrl;
                        a.download = `Sturdfix_WMS_Row_${row}_Slot_Labels.pdf`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(blobUrl);
                      } else { toast.error(data.detail || 'Generation failed'); }
                    } catch { toast.error('Failed to generate row labels'); }
                    setGeneratingAll(false);
                  }}
                  disabled={generatingAll}
                  variant="outline"
                  className="h-10 text-sm font-semibold"
                  style={{ borderColor: ROW_COLORS[row], color: ROW_COLORS[row] }}
                  data-testid={`download-row-${row}-labels`}
                >
                  {generatingAll ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Download size={14} className="mr-2" />}
                  Download Row {row} Labels ({rowSlots[row]} slots)
                </Button>
              );
            })}
          </div>
          
          <Button 
            variant="outline"
            onClick={handleDownloadManual}
            className="h-10 border-[#1a2fce] text-[#1a2fce]"
            data-testid="download-manual-btn"
          >
            <FileText size={16} className="mr-2" />
            Download Label Affixing Manual
          </Button>

          {/* Racks Grid by Row */}
          {['A', 'B', 'C', 'D'].map(row => (
            <Card key={row} className="border-0 shadow-sm overflow-hidden">
              <CardHeader className="pb-2" style={{ borderLeft: `4px solid ${ROW_COLORS[row]}` }}>
                <CardTitle className="text-sm flex items-center gap-2">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: ROW_COLORS[row] }} />
                  Row {row}
                  <Badge variant="outline" className="text-[10px] ml-auto">
                    {racksByRow[row]?.length || 0} racks · {racksByRow[row]?.reduce((s, r) => s + r.total_slots, 0) || 0} slots
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-9 gap-2">
                  {(racksByRow[row] || []).map(rack => (
                    <button
                      key={rack.rack_code}
                      onClick={() => handleDownloadRackLabels(rack.rack_code)}
                      disabled={downloadingRack === rack.rack_code}
                      className="relative p-2 rounded-lg border border-gray-200 hover:border-[#1a2fce] hover:bg-[#1a2fce]/5 transition-all text-left"
                      data-testid={`rack-label-${rack.rack_code}`}
                    >
                      {downloadingRack === rack.rack_code ? (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-lg">
                          <Loader2 size={16} className="animate-spin text-[#1a2fce]" />
                        </div>
                      ) : null}
                      <div className="font-mono font-bold text-sm" style={{ color: ROW_COLORS[row] }}>{rack.rack_code}</div>
                      <div className="text-[10px] text-gray-500">
                        L{rack.levels} × P{rack.positions}
                      </div>
                      <div className="text-[10px] text-gray-400">{rack.total_slots} slots</div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Footer branding */}
          <div className="text-center text-xs text-gray-400 py-2">
            Sturdfix™ WMS · Singhania International Limited
          </div>
        </TabsContent>

        {/* Backup Tab */}
        <TabsContent value="backup" className="space-y-4">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Database size={16} className="text-[#1a2fce]" /> Backup Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-gray-500">
                Automated nightly backups run at 18:30 UTC. Excel exports include all pallets, boxes, inventory, and movement logs with Sturdfix™ WMS branding.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button
                  onClick={async () => {
                    setBackupLoading(true);
                    try {
                      const token = localStorage.getItem('wms_token');
                      const res = await fetch(`${API_BASE}/api/admin/backup-now`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      const data = await res.json();
                      if (data.success) {
                        toast.success('Backup generated successfully');
                      } else {
                        toast.error(data.message || 'Backup failed');
                      }
                    } catch (err) {
                      toast.error('Backup request failed');
                    }
                    setBackupLoading(false);
                  }}
                  disabled={backupLoading}
                  className="h-10 text-sm font-semibold"
                  style={{ backgroundColor: '#0e7a45' }}
                  data-testid="run-backup-now"
                >
                  {backupLoading ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Database size={14} className="mr-2" />}
                  Run Backup Now
                </Button>

                <Button
                  onClick={async () => {
                    setEmailLoading(true);
                    try {
                      const token = localStorage.getItem('wms_token');
                      const res = await fetch(`${API_BASE}/api/admin/test-email`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      const data = await res.json();
                      if (data.success) {
                        toast.success(data.message);
                      } else {
                        toast.error(data.message || 'Email test failed');
                      }
                    } catch (err) {
                      toast.error('Email test request failed');
                    }
                    setEmailLoading(false);
                  }}
                  disabled={emailLoading}
                  variant="outline"
                  className="h-10 text-sm font-semibold"
                  data-testid="send-test-email"
                >
                  {emailLoading ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Mail size={14} className="mr-2" />}
                  Send Test Email
                </Button>
              </div>

              <div className="text-[10px] text-gray-400 mt-2">
                Test email sends to the configured BACKUP_EMAIL_TO address. SMTP credentials must be set in environment variables.
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Data Management Tab */}
        <TabsContent value="data" className="space-y-4">
          <AdminDataTab />
        </TabsContent>

        {/* User Guide Tab */}
        <TabsContent value="guide" className="space-y-4">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <BookOpen size={16} className="text-[#1a2fce]" /> User Manual / Guide
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-gray-500">
                Download the comprehensive Sturdfix™ WMS user manual. Covers all features: Login, Dashboard, Inventory, Packing, QR Scanning, Issue &amp; Dispatch, Reports, Admin Settings, and more.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* English Manual */}
                <Card className="border border-gray-200 shadow-none">
                  <CardContent className="p-4 flex flex-col items-center text-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#1a2fce]/10 flex items-center justify-center">
                      <FileText size={22} className="text-[#1a2fce]" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-900">English Manual</div>
                      <div className="text-[11px] text-gray-500 mt-0.5">Comprehensive guide — all features</div>
                    </div>
                    <Button
                      onClick={() => {
                        const a = document.createElement('a');
                        a.href = `${API_BASE}/api/static/manuals/Sturdfix_WMS_User_Manual_English.pdf`;
                        a.download = 'Sturdfix_WMS_User_Manual_English.pdf';
                        a.target = '_blank';
                        a.rel = 'noopener noreferrer';
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        toast.success('English manual downloaded');
                      }}
                      className="w-full h-9 text-xs font-semibold"
                      style={{ backgroundColor: '#1a2fce' }}
                      data-testid="download-manual-english"
                    >
                      <Download size={14} className="mr-1.5" /> Download English PDF
                    </Button>
                  </CardContent>
                </Card>

                {/* Hindi Manual */}
                <Card className="border border-gray-200 shadow-none">
                  <CardContent className="p-4 flex flex-col items-center text-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#0e7a45]/10 flex items-center justify-center">
                      <FileText size={22} className="text-[#0e7a45]" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-900">Hindi Manual</div>
                      <div className="text-[11px] text-gray-500 mt-0.5">&#x0935;&#x094d;&#x092f;&#x093e;&#x092a;&#x0915; &#x092e;&#x093e;&#x0930;&#x094d;&#x0917;&#x0926;&#x0930;&#x094d;&#x0936;&#x093f;&#x0915;&#x093e; — &#x0938;&#x092d;&#x0940; &#x0938;&#x0941;&#x0935;&#x093f;&#x0927;&#x093e;&#x090f;&#x0902;</div>
                    </div>
                    <Button
                      onClick={() => {
                        const a = document.createElement('a');
                        a.href = `${API_BASE}/api/static/manuals/Sturdfix_WMS_User_Manual_Hindi.pdf`;
                        a.download = 'Sturdfix_WMS_User_Manual_Hindi.pdf';
                        a.target = '_blank';
                        a.rel = 'noopener noreferrer';
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        toast.success('Hindi manual downloaded');
                      }}
                      className="w-full h-9 text-xs font-semibold"
                      style={{ backgroundColor: '#0e7a45' }}
                      data-testid="download-manual-hindi"
                    >
                      <Download size={14} className="mr-1.5" /> Download Hindi PDF
                    </Button>
                  </CardContent>
                </Card>
              </div>

              <div className="text-[10px] text-gray-400 mt-2">
                Manuals cover: Login, Dashboard, Inventory, Packing, QR Scanning, Warehouse Map, Issue &amp; Dispatch, Customers, GRN, Labels, Cycle Count, Returns, Reports, Admin Panel, Offline Mode, and FAQ.
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
